import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { submitDeclaration, getDeclarationStatus, getApplicationId, checkAndTransferLabDetails } from '../services/api';
import { LoaderWrapper } from './index';
import { encryptText, encryptAppId } from '../utils/encryption';

const CorruptionDeclarationPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [agreedToDeclaration, setAgreedToDeclaration] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({
    orgId: '',
    userTypeId: 0,
    appId: null
  });

  const initializeDeclaration = useCallback(async () => {
    try {
      // Get user info from localStorage or session
      const storedOrgId = localStorage.getItem('orgId') || sessionStorage.getItem('orgId');
      const storedUserTypeId = localStorage.getItem('userTypeId') || sessionStorage.getItem('userTypeId') || '0';
      
      if (!storedOrgId) {
        toast.error('Organization information not found. Please login again.');
        navigate('/main/login');
        return;
      }

      setUserInfo({
        orgId: storedOrgId,
        userTypeId: storedUserTypeId,
        appId: null
      });

      // Check if declaration has already been submitted
      try {
        const statusResponse = await getDeclarationStatus(storedOrgId);
        if (statusResponse.data.success && statusResponse.data.hasSubmitted) {
          toast.info('Declaration has already been submitted');
          // Redirect to appropriate page based on user type
          if (storedUserTypeId === '0' || storedUserTypeId === 0) {
            navigate('/main/dashboard');
          } else {
            navigate('/main/assessor');
          }
          return;
        }
      } catch (error) {
        // If error, assume declaration hasn't been submitted and continue
      }

      // Get application ID
      try {
        const appResponse = await getApplicationId(storedOrgId);
        if (appResponse.data.success) {
          setUserInfo(prev => ({
            ...prev,
            appId: appResponse.data.appId
          }));
          localStorage.setItem('appId', appResponse.data.appId); // Store appId for future use
        }
      } catch (error) {
        // Continue without app ID
      }

      // Check if user should be redirected to application form
      const storedLabTypeId = localStorage.getItem('labTypeId');
      if (storedUserTypeId === '4' && storedLabTypeId === '3') {
        // Get appId and orgId for encrypted navigation
        const appId = localStorage.getItem('appId');
        const orgId = localStorage.getItem('orgId');
        
        if (appId && orgId) {
          // Encrypt appId and orgId for URL parameters
          const encryptedAppId = encryptAppId(appId);
          const encryptedOrgId = encryptText(orgId);
          
          // Navigate with encrypted parameters
          navigate(`/main/medical/laboratory-details?appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
        } else {
          // Fallback to navigation without parameters
          navigate('/main/medical/laboratory-details');
        }
        return;
      }

    } catch (error) {
      // Error initializing declaration
    } finally {
      setInitialLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    setMounted(true);
    initializeDeclaration();
  }, [initializeDeclaration]);

  const prohibitedActivities = [
    'Assessor claiming travelling expenditure/local conveyance from the CABs in cash or by any other mode of payment.',
    'NABL prohibits any corrupt practices. Any violation shall lead to adverse action against both the CAB and the assessor.',
    'Seeking undue favors by assessor (viz. travel for personal use, shopping, sightseeing, events, gifts, mementos, job referrals etc.) or CABs offering such unwanted favors.',
    'Involvement in promotional/marketing of any brand/make/model of equipment, machinery, instruments, providing consultancy, training, business interests.'
  ];

  const handleSubmit = () => {
    if (!agreedToDeclaration) {
      toast.error('Please agree to the declaration before proceeding');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmission = async () => {
    setLoading(true);
    setShowConfirmDialog(false);
    
    try {
      const submissionData = {
        orgId: userInfo.orgId,
        userTypeId: userInfo.userTypeId,
        agreed: true
      };

      const response = await submitDeclaration(submissionData);
      
      if (response.data.success) {
        setSubmitted(true);
       // toast.success('Declaration accepted successfully!');
        
        // Check if user should be redirected to application form after declaration
        const storedUserTypeId = localStorage.getItem('userTypeId');
        const storedLabTypeId = localStorage.getItem('labTypeId');
        
        if (storedUserTypeId === '4' && storedLabTypeId === '3') {
          toast.success('Declaration submitted! Redirecting to medical application form...');
          setTimeout(() => {
            // Get appId and orgId from localStorage
            const appId = localStorage.getItem('appId');
            const orgId = localStorage.getItem('orgId');
            
            if (appId && orgId) {
              // Encrypt appId and orgId for URL parameters
              const encryptedAppId = encryptAppId(appId);
              const encryptedOrgId = encryptText(orgId);
              
              // Navigate with encrypted parameters
              navigate(`/main/medical/laboratory-details?appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
            } else {
              // Fallback to navigation without parameters
              navigate('/main/medical/laboratory-details');
            }
          }, 2000);
          return;
        }
        
        // Check if we need to prepare Form153 data
        const labTypeId = response.data.labTypeId;
        let redirectPath = response.data.redirectPath || '/main/dashboard';
        
        // Add encrypted appId and orgId to redirect path
        const appId = localStorage.getItem('appId');
        const orgId = localStorage.getItem('orgId');
        
        if (appId && orgId) {
          const encryptedAppId = encryptAppId(appId);
          const encryptedOrgId = encryptText(orgId);
          
          // Check if redirectPath already has query parameters
          const separator = redirectPath.includes('?') ? '&' : '?';
          redirectPath = `${redirectPath}${separator}appId=${encryptedAppId}&orgId=${encryptedOrgId}`;
        }
        
        // Store labTypeId in localStorage for future use
        if (labTypeId) {
          localStorage.setItem('labTypeId', labTypeId.toString());
        }

        // If redirecting to medical lab details and we have appId, prepare Form153 data
        const shouldPrepareForm153Data = (
          (labTypeId === '3' || labTypeId === 3) && 
          redirectPath.includes('application-form') && 
          userInfo.appId
        );

        if (shouldPrepareForm153Data) {
          try {
            // toast.info('Preparing laboratory data...', { autoClose: 2000 });
            
            const transferResponse = await checkAndTransferLabDetails(userInfo.appId, userInfo.orgId);
            
            if (transferResponse.data.success) {
              if (transferResponse.data.dataExists) {
                // toast.success('Laboratory data is ready!', { autoClose: 2000 });
              } else if (transferResponse.data.transferred) {
                // toast.success('Laboratory data prepared successfully!', { autoClose: 2000 });
              }
            } else {
              // If data transfer fails, still proceed
              // toast.warning('Proceeding without data preparation', { autoClose: 3000 });
            }
          } catch (transferError) {
            // If API call fails, still proceed to avoid blocking user flow
            // toast.warning('Data preparation encountered an issue, but you can still proceed', { autoClose: 3000 });
          }
        }
        
        // Redirect based on the response from backend
        setTimeout(() => {
          if (redirectPath.includes('live_lang_lat')) {
            // Handle location verification redirect
            window.location.href = `/main${redirectPath}`;
          } else if (redirectPath.includes('application-form')) {
            
            // Navigate to dashboard which will check stage and redirect appropriately
            navigate('/main/dashboard');
          }  else if (redirectPath.includes('medical')) {
            // Use the enhanced redirectPath that already contains encrypted parameters
            navigate(redirectPath);
          } else if (redirectPath.includes('assessor')) {
            navigate('/main/assessor');
          } else {
            navigate('/main/dashboard');
          }
        }, shouldPrepareForm153Data ? 3000 : 2000); // Give more time if data preparation occurred
      } else {
        toast.error(response.data.message || 'Failed to submit declaration');
      }
    } catch (error) {
      let errorMessage = 'Failed to submit declaration';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  // Show loading spinner while initializing
  if (initialLoading) {
    return (
      <LoaderWrapper 
        loading={true} 
        type="fullscreen" 
        message="Loading Declaration..." 
      />
    );
  }

  return (
    <Box
      sx={{
        mt: 8,
        height: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="xl">
        <Fade in={mounted} timeout={1000}>
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'white',
              height: '95vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Main Content - Scrollable if needed */}
            <Box sx={{ 
              flex: 1, 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'auto'
            }}>
              {/* Two Column Layout */}
              <Box sx={{ 
               
              }}>
                {/* Left Column - Policy */}
                <Box>
                    <Typography align='center' variant="h4" sx={{ fontWeight: 700, mb: 1.5, color: '#1e3c72' }}>
                      Declaration
                    </Typography>
                    <Divider/>
                  <Alert 
                    severity="error" 
                    icon={<WarningIcon />}
                    sx={{ my: 2, fontSize: '0.9rem' }}
                  >
                    NABL has Zero Tolerance for assessors and CABs indulging in prohibited activities.
                  </Alert>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: '#1e3c72', fontSize: '1.1rem' }}>
                    Prohibited Activities:
                  </Typography>
                  
                  <List dense sx={{ mb: 2 }}>
                    {prohibitedActivities.map((activity, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={activity}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: '0.85rem',
                              lineHeight: 1.4,
                              color: '#374151',
                            } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* Right Column - Additional Info & Declaration */}
                <Box>
                  <Alert severity="warning" sx={{ mb: 3, fontSize: '0.85rem' }}>
                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                    The exchange of gifts, money (in cash or any other mode) or entertainment between CAB and the assessor will be seen as attempts to influence the Assessor which is strictly prohibited by NABL. In order to avoid both real and perceived conflicts of interest, NABL strongly objects the intentional or unintentional exchange of gifts or cash payment or money in any form or any other entertainment during assessment (and at any time before or after the assessment).
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                      Any violation shall draw adverse actions against both CAB and assessor.
                    </Typography>
                  </Alert>

                 <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreedToDeclaration}
                          onChange={(e) => setAgreedToDeclaration(e.target.checked)}
                          sx={{ color: '#1e3c72', '&.Mui-checked': { color: '#1e3c72' } }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                          I have read and understood NABL's Zero Tolerance Policy and agree to comply 
                          with all terms and conditions mentioned above.
                        </Typography>
                      }
                      sx={{ }}
                    /> <br/>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
                      <Button
                        variant="contained"
                        onClick={handleConfirmSubmission}
                        
                        disabled={!agreedToDeclaration || loading}
                        sx={{
                          px: 3,
                          fontSize: '0.9rem',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                          },
                          '&:disabled': {
                            background: '#ccc',
                          },
                        }}
                      >
                        <LoaderWrapper loading={loading} type="button">
                          {loading ? 'Processing...' : 'Submit Declaration'}
                        </LoaderWrapper>
                      </Button>
                      </div>

                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Success State */}
        {submitted && (
          <Fade in={true} timeout={1000}>
            <Paper
              elevation={8}
              sx={{
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'white',
                zIndex: 1000,
                minWidth: 400,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#047857' }}>
                Declaration Submitted Successfully
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Thank you for your commitment to maintaining integrity.
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <SecurityIcon sx={{ fontSize: 28, color: '#1e3c72', mr: 1 }} />
            Confirm Declaration
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1">
              Are you sure you want to submit this declaration?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 2 }}>
            <Button onClick={() => setShowConfirmDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSubmission}
              variant="contained"
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                },
                '&:disabled': {
                  background: '#ccc',
                },
              }}
            >
              <LoaderWrapper loading={loading} type="button">
                {loading ? 'Submitting...' : 'Confirm'}
              </LoaderWrapper>
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CorruptionDeclarationPage;
